// ============= Imports =============
import { SignalRContextProps } from '@/contexts/SignalRContext';
import {
  toastHistoryStore,
  getStackTrace,
} from '@/components/common/Toast/ToastHistory';

// ============= Constants =============
// Hub name must match the hub name configured in environment variables
// e.g., VITE_SIGNALR_HUB_PATH_USER_RELAY_HUB=/hubs/UserRelayHub
const HUB_NAME = 'userRelayHub';

// ============= Types =============
export interface AddClientToGroupProps {
  invokeHub: SignalRContextProps['invokeHub'];
  userId: string;
  workspaceId: string;
}

// ============= Main Function =============
/**
 * Adds the client to a user group in UserRelayHub
 * @param invokeHub Function to invoke SignalR hub methods
 * @param userId The user ID to join
 * @returns Promise with API response or error
 */
export const addClientToGroup = async ({
  invokeHub,
  userId,
  workspaceId,
}: AddClientToGroupProps): Promise<any> => {
  try {
    console.log(`🔄 Adding client to group [${userId}] in UserRelayHub...`);

    // Log outbound invocation (log only; not a user-facing toast)
    try {
      toastHistoryStore.addLogEntry({
        type: 'info',
        message: `[SignalR Invoke] ${HUB_NAME}.AddClientToUsersAsync`,
        direction: 'outgoing',
        source: JSON.stringify(
          {
            hub: HUB_NAME,
            target: 'AddClientToUsersAsync',
            arguments: [workspaceId, userId],
            stack: getStackTrace(),
          },
          null,
          2
        ),
      });
    } catch {
      // Never let logging break the app
    }

    if (!userId) {
      throw new Error('userId is required');
    }

    const result = await invokeHub<any>(HUB_NAME, 'AddClientToUsersAsync', userId);

    console.log(`✅ Client added to group [${userId}] successfully`);
    return result;
  } catch (error) {
    console.error(
      `🔴 Error adding client to group [${userId}] in UserRelayHub:`,
      error
    );

    // Log error (log only; not a user-facing toast)
    try {
      toastHistoryStore.addLogEntry({
        type: 'error',
        message: `[SignalR Invoke Error] ${HUB_NAME}.AddClientToUsersAsync for user ${userId}`,
        direction: 'outgoing',
        source:
          (error instanceof Error && error.stack) ||
          JSON.stringify(
            {
              hub: HUB_NAME,
              target: 'AddClientToUsersAsync',
              arguments: [userId],
              error: String(error),
            },
            null,
            2
          ),
      });
    } catch {
      // Swallow logging errors
    }
    throw error;
  }
};
